import { Box, Button, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectIsHitl7Enabled } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_BAR_CONTROL_HEIGHT } from '../../utils/hitl3Spacing';
import { getIntentButtonColors } from '../../utils/intentButtonStyle';
import { LANGUAGE_SWITCH_INTENT_CARD } from '../../utils/constants';
import LanguageSwitch from '../LangaugeSwitch';
import FlagIssueButton from '../IssueReport/FlagIssueButton';
import { IntentCard, Intents } from '../../types/intent';
import useGroupedIntents from './useGroupedIntents.hooks';
import useIntentHotkeys from './useIntentHotkeys.hooks';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined';

// HITL 3.0 bottom-bar grouping of intents. Each intent is placed in a section
// by its `intent` enum value; anything unmapped falls into a trailing "More"
// group so no card is ever silently dropped. Button background + text colors
// come from the shared getIntentButtonColors helper with isHitl3 = true, which
// applies the solid section scheme (amber / blue / red); the legacy layout keeps
// each card's own colors. The click/dispatch behavior comes from this layout's
// own `useGroupedIntents` hook — independent of the OLD IntentsSection so the
// two can't affect each other.
interface IntentSection {
  label: string;
  intents: Intents[];
  Icon: React.ElementType;
}

const INTENT_SECTIONS: IntentSection[] = [
  {
    label: 'Conversation',
    intents: [
      Intents.greet,
      Intents.acknowledge,
      Intents.askRepeat,
      Intents.askMoment,
    ],
    Icon: ChatBubbleOutlineOutlinedIcon,
  },
  {
    label: 'Order Flow',
    intents: [
      Intents.continue,
      Intents.confirm,
      Intents.readback,
      Intents.complete,
    ],
    Icon: AccountTreeOutlinedIcon,
  },
  {
    label: 'Escalation',
    intents: [Intents.requestStaff],
    Icon: AutoAwesomeOutlinedIcon,
  },
];

const FALLBACK_SECTION: Omit<IntentSection, 'intents'> = {
  label: 'More',
  Icon: ChatBubbleOutlineOutlinedIcon,
};

// The four primaries also have a single-key arrow fast-path (handled in the
// OmniBar). Surfacing the glyph on the pill keeps that otherwise-invisible
// mapping discoverable alongside the Alt+digit badge.
const ARROW_GLYPH: Partial<Record<Intents, string>> = {
  [Intents.acknowledge]: '↑',
  [Intents.askMoment]: '←',
  [Intents.continue]: '→',
  [Intents.readback]: '↓',
};

// Escalation (Request Staff) is pulled to the FRONT of the intent cards so it
// sits directly to the right of the Quick Response input — out of the
// bottom-right corner where it previously stacked under the Cancel / Finish
// Order CTAs (red-on-red, misclick-prone). The remaining, lower-consequence
// groups follow. Derived from the requestStaff intent so a label rename can't
// silently break the ordering.
const ESCALATION_SECTION =
  INTENT_SECTIONS.find((section) =>
    section.intents.includes(Intents.requestStaff)
  )?.label ?? 'Escalation';

const TRAILING_SECTIONS = [
  ...INTENT_SECTIONS.map((section) => section.label).filter(
    (label) => label !== ESCALATION_SECTION
  ),
  FALLBACK_SECTION.label,
];

// Order Flow (Continue / Confirm / Read Back / Complete). Derived from the
// `continue` intent so a label rename can't silently move the OmniBar, which is
// rendered immediately before this section (see render below).
const ORDER_FLOW_SECTION =
  INTENT_SECTIONS.find((section) => section.intents.includes(Intents.continue))
    ?.label ?? 'Order Flow';

/**
 * HITL-1665 — the metrics that buy the bottom row a single line at 1024px.
 *
 * The row needs ~1011px of 1024. Every pixel comes from SPACING and from the
 * typing box having left the row in HITL-1664 — no button is dropped and no
 * breakpoint is introduced, because both were rejected: "Intent buttons
 * splitting" was one of the four original agent complaints, and a breakpoint
 * would mean two layouts to reason about.
 *
 * These are applied as INLINE STYLE, not as new `makeStyles` rules or changed
 * `sx` values, and only when the flag is on. Adding a rule to a live makeStyles
 * block renumbers JSS class names in far-away snapshots, and an `sx` change
 * would alter the emotion hash this component's own snapshot records. An inline
 * style that is `undefined` with the flag off leaves the DOM byte-identical.
 * Same approach HITL-1663 took for the Pulse strip.
 *
 * Invisible at 1440: the eight intent pills take `flex: 1 1 auto` with
 * `min-width: max-content`, so they absorb the ~400px of slack a wide console
 * has (~+50px each) and the tightening only bites when the bar is actually
 * short.
 */
const HITL7_BAR_GAP = '5px'; // was theme.spacing(1) = 8px, between AND within groups
const HITL7_KEYCAP_MARGIN = 0.25; // was 0.5 = 4px → 2px
const HITL7_ESCALATION_PILL_PX = 1; // was 1.25 = 10px → 8px
// The language cluster hugs its content instead of reserving a fixed width.
// The makeStyles rule holds a 150px floor sized for "English" / "Spanish"; the
// segments now read EN / ES (the full name moved to the tooltip), so that floor
// is reserving width the row does not have — it was the last thing pushing the
// bar over 1024. `flexShrink: 0` on the group still stops it being squeezed
// below its content, so 0 here means "hug", not "collapsible".
const HITL7_LANGUAGE_GROUP_STYLE: React.CSSProperties = { minWidth: 0 };

// One flush row that never wraps. `nowrap` is the actual fix for the splitting
// complaint: `groupButtons` wraps by default, which is what put the pills on a
// second line below ~1230px. Paired with `min-width: max-content` on the pills
// so nothing can be squeezed narrower than its label instead.
const HITL7_GROUP_BUTTONS_STYLE: React.CSSProperties = {
  gap: HITL7_BAR_GAP,
  flexWrap: 'nowrap',
};
// Conversation and Order Flow share the leftover width; Escalation keeps
// hugging its single button so "Staff" does not stretch across the bar.
const HITL7_FLEX_GROUP_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  // `max-content`, NOT 0. A flex item's default `min-width: auto` already
  // refuses to shrink below its content; overriding it to 0 is the usual idiom
  // for letting a flex child shrink, and here it was actively wrong. The pills
  // inside carry `min-width: max-content` and cannot shrink, so a group allowed
  // to shrink past them does not compress — its buttons simply overflow its box
  // and run on top of the next group. That is what put Continue over Moment.
  //
  // With this floor the groups keep their content width, so an over-long row
  // overflows the bar visibly instead of quietly overlapping itself.
  minWidth: 'max-content',
};
// Applied to each pill's Tooltip wrapper — the real flex item. `max-content`
// floors it at its own label, so the eight pills share the slack evenly and
// every gap stays the one uniform 5px instead of collecting at a group's edge.
const HITL7_PILL_WRAPPER_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 'max-content',
  display: 'flex',
};

/**
 * HITL-1666 — the arrow fast-path glyph as a CORNER MARK rather than part of the
 * label.
 *
 * Okay ↑, Moment ←, Continue →, Read Back ↓ each have a single-key shortcut. The
 * glyph trailing the label costs ~56px across the four pills, which the row
 * cannot afford at 1024px, and it reads as if the arrow were part of the button's
 * name. Absolutely positioned it leaves the text flow entirely, so the label
 * measures as just the label — the same move the Issue button's count badge
 * makes, and the reason that badge is the shape this follows.
 *
 * Stacking the glyph under the keycap was the free alternative and was rejected
 * on looks.
 */
const HITL7_ARROW_MARK_SX = {
  position: 'absolute',
  right: '3px',
  bottom: '1px',
  fontSize: '0.56rem',
  opacity: 0.8,
  lineHeight: 1,
  // The mark is a legend, not a target: a press must land on the button
  // underneath it, not on the glyph.
  pointerEvents: 'none',
} as const;

// The base inset stays 6px on the left; the right side pays 4px more, and ONLY
// on these four pills.
//
// That 4px is not padding for the mark itself — the mark is out of the flow and
// costs no width. It is clearance for a DESCENDER: "Okay" is the only one of the
// four with one, and the tail of its `y` lands exactly where the mark sits.
const HITL7_PILL_PL = '6px';
const HITL7_ARROW_PILL_PR = '10px';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    // One flush row (no per-group cards): Escalation · Language · Conversation ·
    // Quick Response · Order Flow · More. The embedded input takes the leftover
    // width; the groups hug their buttons.
    flex: '1 1 auto',
    minWidth: 0,
    gap: theme.spacing(1),
    alignItems: 'stretch',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    // Flush cluster — no card frame. Hugs the buttons it holds (no min-width /
    // flex-grow) so a single-button group like Escalation leaves no trailing
    // space; still shrinkable as a graceful fallback on narrow windows.
  },
  groupButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignContent: 'flex-start',
    alignItems: 'center',
    flex: 1,
  },
  // Language-switch cluster, sibling to the intent groups. Only rendered for
  // bilingual restaurants. Flush (no card frame) — the toggle itself carries the
  // border now. Sized to its content (not flex-grown).
  languageGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexShrink: 0,
    minWidth: 150,
  },
  languageSwitch: {
    display: 'flex',
    // White track with a hairline border so the English / Spanish toggle reads
    // as a segmented control on the flush white bar. A bit larger than the
    // intent pills so it's easier to hit.
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(2.5),
    padding: '3px',
    height: 40,
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    width: '100%',
  },
  languageOption: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    borderRadius: theme.spacing(2.25),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 'bold',
    // Title-case the 'english' / 'spanish' values → "English" / "Spanish".
    textTransform: 'capitalize',
    zIndex: 1,
    '&.disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  languageOptionActive: {
    backgroundColor: 'rgb(0, 107, 166)',
    color: 'white',
  },
  languageOptionInactive: {
    backgroundColor: 'transparent',
    color: '#4f5153',
  },
}));

interface IntentsSectionGroupedProps {
  /**
   * Quick Response input (OmniBar) node. Rendered inside the bar immediately
   * before the Order Flow group so it sits between the Conversation primaries
   * and Continue / Confirm / Read Back / Complete. Omit to render the intent
   * groups only.
   */
  omniBar?: React.ReactNode;
}

export default function IntentsSectionGrouped({
  omniBar,
}: IntentsSectionGroupedProps = {}) {
  const classes = useStyles();
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const {
    intents,
    intentsStatus,
    isBilingual,
    isSpanish,
    isStaffInterventionHappening,
    onIntentDown,
    onIntentUp,
    fireIntentByShortcut,
    shortcutNumberByIdx,
    languageSwitch,
  } = useGroupedIntents();

  // Alt+1…N mirror the on-pill number badges (contiguous over reachable intents).
  useIntentHotkeys(fireIntentByShortcut);

  // Keep each intent's original (filtered) index so click handling and
  // intentsStatus lookups stay identical to the flat layout.
  const intentsWithIdx = intents.map((intent, idx) => ({ intent, idx }));

  const sectionFor = (intent: Intents) =>
    INTENT_SECTIONS.find((section) => section.intents.includes(intent))
      ?.label ?? FALLBACK_SECTION.label;

  const groupedIntents = intentsWithIdx.reduce<
    Record<string, { intent: IntentCard; idx: number }[]>
  >((groups, entry) => {
    const label = sectionFor(entry.intent.intent);
    (groups[label] = groups[label] ?? []).push(entry);
    return groups;
  }, {});

  const renderGroup = (label: string) => {
    const entries = groupedIntents[label];
    if (!entries?.length) return null;
    // Escalation (Request Staff) is the one high-consequence control in the bar,
    // so it keeps extra emphasis — uppercase label, raised-hand icon and heavier
    // weight. Its colors (solid red) come from the shared intent-button helper,
    // matching the legacy layout.
    const isEscalation = label === ESCALATION_SECTION;
    return (
      <div
        key={label}
        className={classes.group}
        style={isHitl7 && !isEscalation ? HITL7_FLEX_GROUP_STYLE : undefined}
      >
        <div
          className={classes.groupButtons}
          style={isHitl7 ? HITL7_GROUP_BUTTONS_STYLE : undefined}
        >
          {entries.map(({ intent: intentCard, idx }) => {
            const clickable = !!intentsStatus[idx]?.clickable;
            // Background + text come from the shared getIntentButtonColors helper
            // with isHitl3 = true → the solid section scheme (amber Conversation,
            // blue Order Flow, red Escalation). Only the structural affordances
            // (size, grouping, Alt+digit keycap, arrow/raised-hand glyphs) are
            // specific to this layout.
            const { backgroundColor, color } = getIntentButtonColors(
              intentCard,
              clickable,
              true,
              isHitl7
            );
            // One solid style for every pill. Escalation (Request Staff) only
            // adds emphasis — uppercase, heavier weight and the raised-hand icon
            // — not a different color treatment: it is solid red like legacy.
            // Alt+number badge: the intent's contiguous shortcut number (Request
            // Staff has none → no badge). Layout is uniform across pills:
            // [number] [label], with the arrow as a corner mark under 7.0.
            const shortcutNumber = shortcutNumberByIdx[idx];
            const showBadge = !isEscalation && shortcutNumber !== undefined;
            const arrowGlyph = !isEscalation
              ? ARROW_GLYPH[intentCard.intent]
              : undefined;
            // HITL-1666: only these four pills leave the text flow, and only
            // they pay the extra right padding for it.
            const hasCornerArrow = isHitl7 && !!arrowGlyph;
            const buttonSx = {
              height: HITL3_BAR_CONTROL_HEIGHT,
              // The eight non-escalation pills grow into a wide console's slack
              // and shrink no further than their own label, so the tightened
              // metrics are invisible at 1440 and no breakpoint is needed.
              flex: isHitl7 && !isEscalation ? '1 1 auto' : '0 0 auto',
              minWidth: isHitl7 && !isEscalation ? 'max-content' : 64,
              ...(hasCornerArrow
                ? {
                    // Asymmetric on purpose — see HITL7_ARROW_PILL_PR.
                    pl: HITL7_PILL_PL,
                    pr: HITL7_ARROW_PILL_PR,
                    // The mark is positioned against the button. MUI's
                    // ButtonBase already sets this for its ripple, but relying
                    // on that would make the mark's placement depend on a
                    // library internal.
                    position: 'relative',
                  }
                : {
                    px:
                      isEscalation && isHitl7
                        ? HITL7_ESCALATION_PILL_PX
                        : isEscalation
                        ? 1.25
                        : 0.75,
                  }),
              borderRadius: '4px',
              backgroundColor,
              color,
              textTransform: isEscalation ? 'uppercase' : 'none',
              letterSpacing: isEscalation ? '0.04em' : 'normal',
              fontWeight: isEscalation ? 700 : 500,
              fontSize: HITL3_FONT_SIZE,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              // Staff intervention fades every pill to signal it's inert (the
              // click/TTS path is already blocked in useGroupedIntents). The
              // Mui-disabled override below preserves the group color, so only
              // this opacity communicates the disabled state.
              opacity: isStaffInterventionHappening ? 0.5 : 1,
              '& .MuiButton-startIcon': { marginRight: '4px' },
              '&:hover': { backgroundColor },
              // Keep the group's text color on the shared disabled grey (parity
              // with legacy's inline style) instead of MUI's greyed-out default.
              '&.Mui-disabled': { backgroundColor, color },
            };
            const keyHint = showBadge
              ? `Alt+${shortcutNumber}${arrowGlyph ? ` · ${arrowGlyph}` : ''}`
              : '';
            // The keycap is a WHITE chip, so its number needs a dark-enough ink.
            // For dark buttons (white text) the button color reads fine on white;
            // for light buttons (the amber Conversation group, which use dark
            // text) the button color (amber) would be nearly invisible, so fall
            // back to near-black — keeps the 1–4 numbers legible.
            const keycapInk =
              color === Colors.white
                ? backgroundColor
                : Colors.primaryButtonTextColor;
            const keycapSx = {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 15,
              height: 15,
              px: '3px',
              mr: isHitl7 ? HITL7_KEYCAP_MARGIN : 0.5,
              borderRadius: '3px',
              border: `1px solid ${Colors.white}`,
              backgroundColor: Colors.white,
              color: keycapInk,
              fontSize: '0.62rem',
              fontWeight: 700,
              lineHeight: 1,
            };
            return (
              // Tooltip needs a non-disabled wrapper (the span) to receive hover
              // events while the pill is in its cooldown (disabled) state.
              <Tooltip
                key={`intent-${idx}`}
                title={keyHint}
                arrow
                disableInteractive
              >
                {/* The SPAN is the flex item, not the Button — so the growth
                    has to be set here. Putting `flex` on the Button instead
                    leaves every pill sized to its label and dumps the group's
                    slack as dead space at its trailing edge, which reads as one
                    big gap between Moment and Continue. */}
                <span
                  style={
                    isHitl7 && !isEscalation
                      ? HITL7_PILL_WRAPPER_STYLE
                      : undefined
                  }
                >
                  <Button
                    onMouseDown={() => onIntentDown()}
                    onMouseUp={() => onIntentUp(intentCard, idx)}
                    disableElevation
                    disabled={!clickable || isStaffInterventionHappening}
                    startIcon={
                      isEscalation ? (
                        <PanToolOutlinedIcon sx={{ fontSize: 15 }} />
                      ) : undefined
                    }
                    // MUI Button's own emotion styles override makeStyles
                    // classes, so visual styling lives in `sx`.
                    sx={buttonSx}
                  >
                    {showBadge && (
                      <Box component="span" sx={keycapSx}>
                        {shortcutNumber}
                      </Box>
                    )}
                    {/* HITL 3.0 shows the escalation pill as just "Staff" (the
                        bar is space-tight and the raised-hand icon already reads
                        as "request"). The shared `title` constant stays
                        "Request Staff" — it's an identity key for escalation
                        logic elsewhere — so only the display label changes. */}
                    {intentCard.intent === Intents.requestStaff
                      ? 'Staff'
                      : intentCard.title}
                    {arrowGlyph && (
                      <Box
                        component="span"
                        sx={
                          hasCornerArrow
                            ? HITL7_ARROW_MARK_SX
                            : { fontSize: '0.66rem', ml: 0.5, opacity: 0.85 }
                        }
                      >
                        {arrowGlyph}
                      </Box>
                    )}
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      id="intentsSection"
      className={classes.container}
      // Same 5px between groups as within them — the AC's "uniform across every
      // button" is one gap value, not two that happen to look close.
      style={isHitl7 ? { gap: HITL7_BAR_GAP } : undefined}
    >
      {/* Escalation stays leftmost — out of the red-on-red bottom-right corner. */}
      {renderGroup(ESCALATION_SECTION)}
      {/* HITL-1499: not an Intent — deliberately outside INTENT_SECTIONS so the
          Alt+1…9 badges cannot renumber. It owns its own group wrapper and
          renders NOTHING unless issue reporting is on, so the bar is unchanged
          with the flag off. */}
      <FlagIssueButton />
      {isBilingual && (
        <div
          className={classes.languageGroup}
          style={isHitl7 ? HITL7_LANGUAGE_GROUP_STYLE : undefined}
        >
          {/* Stays in the bottom bar. Moving it to the header was proposed —
              it is the only stateful control among momentary buttons, and it
              would have freed 98px, fixing 1024 outright — and rejected. */}
          <LanguageSwitch
            classes={classes}
            isSpanish={isSpanish}
            onIntentUp={languageSwitch}
            intentCard={LANGUAGE_SWITCH_INTENT_CARD}
            compact={isHitl7}
          />
        </div>
      )}
      {TRAILING_SECTIONS.map((label) => (
        <React.Fragment key={label}>
          {/* Quick Response input immediately precedes the Order Flow primaries
              (Continue / Confirm / Read Back / Complete). */}
          {label === ORDER_FLOW_SECTION && omniBar}
          {renderGroup(label)}
        </React.Fragment>
      ))}
    </div>
  );
}
